import { gql, useMutation } from '@apollo/client';
import { RoleAssignmentMode } from '~/types';
import { TASK_RESOURCE_ESTIMATES_LIST_QUERY } from '../TaskResourceEstimates/hooks/useTaskResourceEstimates';

const UPDATE_TASK_ROLE_ASSIGNMENTS = gql`
  mutation UpdateTaskRoleAssignments($input: UpdateTaskRoleAssignmentInput!) {
    updateTaskRoleAssignments(input: $input) {
      taskId
    }
  }
`;

export const taskRoleAssignmentSummaryFragment = gql`
  fragment TaskRoleAssignmentSummary on Task {
    taskRoleAssignmentSummary {
      roleAssignmentMode
      defaultRole {
        id
        displayText
      }
      totalRoleCount
    }
  }
`;

export const updateCache = ({
  taskUri,
  mode,
  defaultRole,
  totalRoleCount
}) => cache => {
  cache.writeFragment({
    id: cache.identify({ __typename: 'Task', id: taskUri }),
    fragment: taskRoleAssignmentSummaryFragment,
    fragmentName: 'TaskRoleAssignmentSummary',
    data: {
      taskRoleAssignmentSummary: {
        __typename: 'TaskRoleAssignmentSummary',
        roleAssignmentMode: mode,
        defaultRole,
        totalRoleCount
      }
    }
  });
};

const usePutTaskRoleAssignments = () => {
  const [mutate, { loading }] = useMutation(UPDATE_TASK_ROLE_ASSIGNMENTS);

  const putTaskRoleAssignments = ({
    taskUri,
    mode,
    rolesToAssign,
    roleUrisToUnassign,
    defaultRole,
    totalRoleCount
  }) => {
    const isSpecificRoles = mode === RoleAssignmentMode.SpecificRoles;

    return mutate({
      variables: {
        input: {
          taskUri,
          roleAssignmentMode: mode,
          specificRolesToBeAssigned: isSpecificRoles
            ? rolesToAssign.map(({ roleUri, isDefault }) => ({
                roleUri,
                isDefault
              }))
            : null,
          specificRoleUrisToBeUnassigned: isSpecificRoles
            ? [...roleUrisToUnassign]
            : null
        }
      },
      update: updateCache({ taskUri, mode, defaultRole, totalRoleCount }),
      refetchQueries: [TASK_RESOURCE_ESTIMATES_LIST_QUERY]
    });
  };

  return { putTaskRoleAssignments, loading };
};

export default usePutTaskRoleAssignments;
